import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Lock, BookOpen, CheckCircle, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ActivationBriefingCard
 * ----------------------
 * Read-receipt step for waitlist members before the referral link unlocks.
 *
 * Props:
 *   waitlistToken: string  - the bbc_waitlist_token (waitlist_member scope)
 *   onAcknowledged: () => void  - called once ack is successfully recorded
 *   previewConfig: object  - OPTIONAL. When passed, the card renders the given
 *                            draft config in admin preview mode: no API calls,
 *                            submit is a no-op, a "Preview as member" banner
 *                            is shown at the top.
 */
const ActivationBriefingCard = ({ waitlistToken, onAcknowledged, previewConfig }) => {
  const [config, setConfig] = useState(previewConfig || null);
  const [status, setStatus] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showFull, setShowFull] = useState(!!previewConfig);  // expand by default in preview

  const isPreview = !!previewConfig;
  const auth = waitlistToken ? { Authorization: `Bearer ${waitlistToken}` } : {};

  const loadAll = async () => {
    try {
      const cfgRes = await axios.get(`${API}/public/waitlist/briefing`);
      if (cfgRes.data?.ok) setConfig(cfgRes.data.config);
      if (waitlistToken) {
        const stRes = await axios.get(`${API}/member/waitlist/briefing/status`, { headers: auth });
        if (stRes.data?.ok) setStatus(stRes.data);
      }
    } catch (e) {
      /* silent — UI will show a fallback */
    }
  };

  useEffect(() => {
    // In preview mode, keep using the passed config and skip API calls entirely
    if (isPreview) {
      setConfig(previewConfig);
      return;
    }
    loadAll();
  }, [waitlistToken, isPreview, previewConfig]);

  const allRequiredChecked =
    config?.acknowledgments?.every((a) => !a.required || accepted[a.key]) ?? false;

  const handleAcknowledge = async () => {
    if (!allRequiredChecked) {
      toast.error('Please confirm all required items first.');
      return;
    }
    if (isPreview) {
      // Preview mode: simulate success but do nothing server-side
      toast.success('Preview only — no acknowledgment recorded.');
      return;
    }
    setSubmitting(true);
    try {
      const acceptedKeys = Object.entries(accepted).filter(([, v]) => v).map(([k]) => k);
      const res = await axios.post(
        `${API}/member/waitlist/briefing/acknowledge`,
        { accepted_keys: acceptedKeys, briefing_version: config?.version },
        { headers: auth },
      );
      if (res.data?.ok) {
        toast.success(config?.success_message || 'Briefing complete. Your referral link is now active.');
        if (onAcknowledged) onAcknowledged();
        // Refresh status
        loadAll();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Could not record acknowledgment');
    } finally {
      setSubmitting(false);
    }
  };

  // If we know the user already acknowledged the CURRENT version, show a compact "done" pill
  // (Skipped in preview mode — admin always wants to see the full card form.)
  if (!isPreview && status && status.acknowledged && !status.requires_reack) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="briefing-card-acknowledged">
        <CardContent className="py-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-sm">Activation Briefing complete</div>
            <div className="text-xs text-muted-foreground">
              You acknowledged version <code>{status.last_version}</code>. Your referral link is active.
            </div>
          </div>
          <Badge variant="outline" className="border-emerald-300 text-emerald-700">
            <ShieldCheck className="h-3 w-3 mr-1" />Verified
          </Badge>
        </CardContent>
      </Card>
    );
  }

  if (!config) {
    return (
      <Card className="border-dashed" data-testid="briefing-card-loading">
        <CardContent className="py-6 text-sm text-muted-foreground text-center">
          Loading briefing…
        </CardContent>
      </Card>
    );
  }

  const needsReack = status?.requires_reack && status?.last_version;

  return (
    <div className={isPreview ? 'space-y-3' : ''} data-testid={isPreview ? 'briefing-preview-wrap' : undefined}>
      {isPreview && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm"
          data-testid="briefing-preview-banner"
        >
          <div className="flex items-center gap-2 text-indigo-800">
            <ShieldCheck className="h-4 w-4" />
            <span className="font-medium">Preview as member</span>
            <span className="text-xs text-indigo-700">
              This is exactly what a waitlist user sees. Nothing is saved or recorded.
            </span>
          </div>
          <Badge variant="outline" className="border-indigo-300 text-indigo-700">DRAFT</Badge>
        </div>
      )}

      <Card className="border-amber-300 bg-amber-50/40" data-testid="briefing-card-required">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Lock className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg">{config.title} Required</CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1">
              {needsReack
                ? `The briefing was updated to ${config.version}. Please re-acknowledge to keep your referral link active.`
                : (config.subtitle || 'A quick builder briefing before your referral link unlocks.')}
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">{config.version.startsWith('v') ? config.version : `v${config.version}`}</Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Intro */}
        <p className="text-sm text-slate-700">{config.intro}</p>

        {/* Read full briefing toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFull(!showFull)}
          data-testid="briefing-toggle-full"
        >
          <BookOpen className="h-3 w-3 mr-2" />
          {showFull ? 'Hide briefing details' : 'Read the full briefing'}
        </Button>

        {showFull && (
          <div className="space-y-3 border-l-2 border-amber-300 pl-4" data-testid="briefing-full-sections">
            {(config.sections || []).map((s, i) => (
              <div key={i} data-testid={`briefing-section-${i}`}>
                <div className="font-medium text-sm">{s.title}</div>
                <p className="text-sm text-slate-700 leading-relaxed mt-1">{s.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* Acknowledgments */}
        <div className="space-y-2 pt-2" data-testid="briefing-ack-list">
          <div className="font-medium text-sm">Please confirm each statement:</div>
          {(config.acknowledgments || []).map((a, i) => (
            <label
              key={a.key}
              htmlFor={`ack-${a.key}`}
              className="flex items-start gap-3 p-3 border rounded-lg bg-white hover:bg-amber-50/50 cursor-pointer"
              data-testid={`briefing-ack-row-${a.key}`}
            >
              <Checkbox
                id={`ack-${a.key}`}
                checked={!!accepted[a.key]}
                onCheckedChange={(v) => setAccepted((p) => ({ ...p, [a.key]: !!v }))}
                data-testid={`briefing-ack-checkbox-${a.key}`}
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700 leading-snug">
                {i + 1}. {a.label}
              </span>
            </label>
          ))}
        </div>

        {/* CTA */}
        <Button
          onClick={handleAcknowledge}
          disabled={!allRequiredChecked || submitting}
          className="w-full sm:w-auto"
          data-testid="briefing-acknowledge-btn"
        >
          {submitting ? 'Submitting…' : (config.button_label || 'I Understand — Unlock My Referral Link')}
        </Button>

        <p className="text-[11px] text-muted-foreground">
          We record your acknowledgment along with the briefing version and timestamp for compliance.
        </p>
      </CardContent>
    </Card>
    </div>
  );
};

export default ActivationBriefingCard;
